import { useCallback, useMemo } from 'react';
import { useBrandsControllerGetClientBrandProfile } from '@/apis/backend/generated/brands/brands';
import { useMediaLibraryControllerGetAssets } from '@/apis/backend/generated/media-library/media-library';
import { useSystemBrandDefaultsControllerGet } from '@/apis/backend/generated/system-brand-defaults/system-brand-defaults';
import { useV1ProductControllerListProducts } from '@/apis/backend/generated/products/products';
import type { BrandResolverContext, PickedProduct } from '@nexmail/shared-util/email-builder';
import { buildBrandResolverContext } from '@/lib/brand-resolver-context';

export interface TemplatePreviewProduct {
  variantId?: string;
  productId?: string;
  productTitle?: string;
  variantPrice?: number | null;
  variantCompareAtPrice?: number | null;
  variantImageUrl?: string | null;
  productUrl?: string | null;
}

export interface TemplatePreviewContext {
  brand: BrandResolverContext | undefined;
  mediaUrls: string[];
  // How many of mediaUrls are the brand's own assets (vs the system stock
  // tail). 0 means previews are running entirely on stock imagery — callers
  // can surface an "add brand images" hint off this.
  brandMediaCount: number;
  transparentMediaUrls: string[];
  productVariantIds: string[];
  // Raw product rows backing the pickers, exposed so callers can thread the
  // same fetch into buildBrandResolverContext (third arg) for $product[N]
  // resolution without issuing a parallel query.
  productItems: TemplatePreviewProduct[];
  // Same callback shape EmailBuilder hands SectionLibraryPanel — rotate
  // through a per-client pool, dedupe within a single tree's resolve.
  pickRecentMedia: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  // Cutout-aware picker. Returns a transparent / product-cutout asset for
  // slots that feed image nodes flagged `productCutout: true`. Resolver
  // falls through to pickRecentMedia when this returns undefined.
  pickTransparentMedia: (slotName: string, used: Set<string>, offset?: number) => string | undefined;
  pickProduct: (slotName: string, used: Set<string>) => string | undefined;
  // Multi-output product picker. Returns a fully-hydrated PickedProduct so the
  // preview slot resolver can dispatch dotted refs (`$slot.productN.name` /
  // `.price` / `.cutoutImage` / `.productUrl`) off a single picked product.
  // transparentImageUrl is paired by index from the brand's transparent pool —
  // the preview can't match by productId because the media list endpoint
  // doesn't expose it; the saved-doc filler does the real cache lookup.
  pickProductRich: (slotName: string, used: Set<string>) => PickedProduct | undefined;
  isLoading: boolean;
}

// Quick URL-extension check for GIF detection. Used to filter GIFs out of
// the auto-fill media pool — animated GIFs render unpredictably across
// email clients and thumbnails capture a random frame. Handles both plain
// extensions and querystring/hash suffixes.
function isLikelyGifUrl(url: string): boolean {
  if (!url) return false;
  const lower = url.toLowerCase();
  const path = lower.split(/[?#]/)[0];
  return path.endsWith('.gif');
}

// Bundles the four data sources the preview pane needs into a single hook so
// the dialog body stays small. Single source of the picker callbacks for
// every surface that resolves $brand / $slot / $product refs client-side
// (template dialogs, the EmailBuilder section library, the canvas Sections
// tab) — keep pool policy changes here so previews and inserts stay in sync.
export function useTemplatePreviewContext(
  orgId: string,
  clientId: string,
  enabled: boolean,
): TemplatePreviewContext {
  const { data: systemDefaults, isLoading: defaultsLoading } = useSystemBrandDefaultsControllerGet({
    query: { enabled, staleTime: 5 * 60 * 1000 },
  });

  const { data: brandProfile, isLoading: brandLoading } = useBrandsControllerGetClientBrandProfile(
    orgId,
    clientId,
    { query: { enabled: enabled && !!orgId && !!clientId } },
  );

  const { data: mediaData, isLoading: mediaLoading } = useMediaLibraryControllerGetAssets(
    orgId,
    clientId,
    { limit: 24 },
    {
      query: {
        enabled: enabled && !!orgId && !!clientId,
        staleTime: 2 * 60 * 1000,
      },
    },
  );

  const { data: productsData, isLoading: productsLoading } = useV1ProductControllerListProducts(
    orgId,
    clientId,
    { limit: 12 },
    {
      query: {
        enabled: enabled && !!orgId && !!clientId,
        staleTime: 2 * 60 * 1000,
      },
    },
  );

  const productItems = useMemo<TemplatePreviewProduct[]>(
    () => (productsData?.data as TemplatePreviewProduct[] | undefined) ?? [],
    [productsData],
  );

  type AssetShape = {
    publicUrl?: string;
    source?: string;
    tags?: string[];
    metadata?: { hasTransparency?: unknown } | null;
  };

  // True when an asset is a transparent product cutout — either tagged that
  // way at upload (`product-cutout` / `transparent` are stamped by the
  // smart-image-gen pipeline) or carries a hasTransparency:true metadata
  // flag from PhotoRoom bg-removal. Either signal is sufficient.
  const isTransparentAsset = (a: AssetShape): boolean => {
    const tags = a.tags ?? [];
    if (tags.includes('transparent') || tags.includes('product-cutout')) return true;
    const meta = a.metadata;
    if (meta && typeof meta === 'object' && meta.hasTransparency === true) return true;
    return false;
  };

  const assets = useMemo<AssetShape[]>(
    () => (mediaData?.assets as AssetShape[] | undefined) ?? [],
    [mediaData],
  );

  const { mediaUrls, brandMediaCount } = useMemo(() => {
    // Exclude transparent / product-cutout assets from the general pool —
    // those are reserved for slots that specifically ask for transparent
    // media (pickTransparentMedia path). Mixing them in means a lifestyle /
    // hero slot can land on a transparent product PNG, which never looks
    // right (cutouts need a designed background).
    const opaque = assets.filter((a) => !isTransparentAsset(a));
    // Prefer uploads / AI-generated as the headline pool (matches the
    // EmailBuilder section-library policy), but fall back to ANY opaque
    // image the brand has if those two buckets are empty — preview should
    // show real brand imagery whenever we can rather than picsum.
    const all = opaque
      .map((a) => a.publicUrl)
      .filter((u): u is string => typeof u === 'string' && u.length > 0);
    const preferred = opaque
      .filter((a) => a.source === 'upload' || a.source === 'ai_generated')
      .map((a) => a.publicUrl)
      .filter((u): u is string => typeof u === 'string' && u.length > 0);
    // Cap at the 10 most-recent so previews cycle through a tight set of
    // current brand imagery instead of dredging up dozens of older assets.
    // Skip GIFs: animated GIFs render inconsistently across email clients
    // and a thumbnail shows a random frame anyway — users who need a
    // specific GIF pick it through the media picker directly.
    const brandUrls = (preferred.length > 0 ? preferred : all)
      .filter((u) => !isLikelyGifUrl(u))
      .slice(0, 10);
    const stockUrls = (systemDefaults?.stockImageUrls ?? []).filter((u) => !isLikelyGifUrl(u));
    return { mediaUrls: [...brandUrls, ...stockUrls], brandMediaCount: brandUrls.length };
  }, [assets, systemDefaults]);

  const brand = useMemo<BrandResolverContext | undefined>(() => {
    // Hand the products fetch through to the brand resolver so chat-template
    // previews can resolve `$product[N].(name|price|imageUrl|url)`. Without
    // this they stay as literal text in the iframe.
    const base = buildBrandResolverContext(brandProfile, systemDefaults, productItems);
    if (!base?.collections || base.collections.length === 0) return base;
    // Many Shopify smart-collections (e.g. "All Products") sync with a null
    // imageUrl. Splice a brand recent-media URL in by collection index so the
    // chat preview doesn't show a broken image where the merge tag landed.
    // Server-side slicer mirrors this so the same image lands at send time.
    if (mediaUrls.length === 0) return base;
    return {
      ...base,
      collections: base.collections.map((c, i) => ({
        ...c,
        imageUrl: c.imageUrl ?? mediaUrls[i % mediaUrls.length],
      })),
    };
  }, [brandProfile, systemDefaults, productItems, mediaUrls]);

  const transparentMediaUrls = useMemo<string[]>(() => {
    return assets
      .filter(isTransparentAsset)
      .map((a) => a.publicUrl)
      .filter((u): u is string => typeof u === 'string' && u.length > 0);
  }, [assets]);

  const productVariantIds = useMemo<string[]>(() => {
    return productItems
      .map((p) => p.variantId)
      .filter((v): v is string => typeof v === 'string' && v.length > 0);
  }, [productItems]);

  const pickRecentMedia = useCallback(
    (_slotName: string, used: Set<string>, offset = 0): string | undefined => {
      const pool = mediaUrls;
      if (pool.length === 0) return undefined;
      for (let i = 0; i < pool.length; i++) {
        const url = pool[(offset + i) % pool.length];
        if (!used.has(url)) return url;
      }
      // Pool exhausted within this resolve — recycle from the offset start
      // rather than falling through to undefined (which leaves an obvious
      // placeholder gap in the preview).
      used.clear();
      return pool[offset % pool.length];
    },
    [mediaUrls],
  );

  const pickTransparentMedia = useCallback(
    (_slotName: string, used: Set<string>, offset = 0): string | undefined => {
      const pool = transparentMediaUrls;
      if (pool.length === 0) return undefined;
      for (let i = 0; i < pool.length; i++) {
        const url = pool[(offset + i) % pool.length];
        if (!used.has(url)) return url;
      }
      // Pool exhausted — recycle from the offset start, same policy as
      // pickRecentMedia, so per-resolve dedupe never blocks a fill.
      return pool[offset % pool.length];
    },
    [transparentMediaUrls],
  );

  const pickProduct = useCallback(
    (_slotName: string, used: Set<string>): string | undefined => {
      for (const id of productVariantIds) {
        if (!used.has(id)) return id;
      }
      return undefined;
    },
    [productVariantIds],
  );

  const brandWebsite = brand?.website;
  const pickProductRich = useCallback(
    (_slotName: string, used: Set<string>): PickedProduct | undefined => {
      for (let i = 0; i < productItems.length; i++) {
        const p = productItems[i];
        if (!p.variantId || !p.productId || used.has(p.variantId)) continue;
        const fallbackImageUrl = p.variantImageUrl ?? '';
        // Pair by index against the cutout pool so each card lands on a
        // distinct transparent asset — approximate, but the saved-doc filler
        // does the real productId-keyed lookup at send time.
        const transparentImageUrl = transparentMediaUrls[i] ?? undefined;
        return {
          productId: p.productId,
          variantId: p.variantId,
          name: p.productTitle ?? '',
          price: p.variantPrice != null ? `$${Number(p.variantPrice).toFixed(2)}` : undefined,
          compareAtPrice:
            p.variantCompareAtPrice != null
              ? `$${Number(p.variantCompareAtPrice).toFixed(2)}`
              : undefined,
          fallbackImageUrl,
          transparentImageUrl,
          // Real storefront URL when the catalog knows it; brand site as the
          // graceful fallback so CTAs still land somewhere sensible.
          productUrl: p.productUrl ?? brandWebsite ?? '#',
        };
      }
      return undefined;
    },
    [productItems, transparentMediaUrls, brandWebsite],
  );

  return {
    brand,
    mediaUrls,
    brandMediaCount,
    transparentMediaUrls,
    productVariantIds,
    productItems,
    pickRecentMedia,
    pickTransparentMedia,
    pickProduct,
    pickProductRich,
    isLoading: defaultsLoading || brandLoading || mediaLoading || productsLoading,
  };
}
